import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Email,
  CheckCircle,
  Error,
  Visibility,
  VisibilityOff,
  Launch,
  Send,
  CloudUpload,
  CheckCircleOutline,
} from '@mui/icons-material';
import api, { emailChannelsAPI } from '../services/api';
import TemplatePreviewModal from './TemplatePreviewModal';
import TestEmailDialog from './TestEmailDialog';

interface EmailChannelData {
  name: string;
  provider: string;
  email_address: string;
  display_name?: string;
  signature?: string;
  provider_config: Record<string, any>;
  greeting_enabled: boolean;
  greeting_message: string;
  auto_assignment_enabled: boolean;
  auto_reply_enabled: boolean;
  poll_interval: number;

  // Template configuration fields
  email_template_type: 'professional' | 'casual' | 'custom';
  response_tone: 'friendly' | 'formal' | 'neutral';
  signature_position: 'top' | 'bottom' | 'none';
  include_branding: boolean;
  custom_template_html: string;
  custom_template_text: string;
  template_variables: Record<string, string>;
}

interface Provider {
  name: string;
  description: string;
  features: string[];
  setup_complexity: string;
  documentation_url: string;
  use_cases?: string;
}

interface ConfigField {
  type: string;
  label: string;
  required: boolean;
  default?: any;
  description?: string;
  options?: { value: string; label: string; }[];
}

const steps = [
  'Provider Selection',
  'Email Configuration',
  'Template Configuration',
  'Channel Settings',
  'Verification'
];

interface EmailChannelConfigProps {
  channelId?: number;  // If provided, component is in edit mode
  onClose?: () => void;  // Close handler
}

const EmailChannelConfig: React.FC<EmailChannelConfigProps> = ({ channelId, onClose }) => {
  const isEditMode = !!channelId;
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  
  const [providers, setProviders] = useState<Record<string, Provider>>({});
  const [configFields, setConfigFields] = useState<Record<string, ConfigField>>({});
  const [createdChannel, setCreatedChannel] = useState<any>(null);
  const [verificationResult, setVerificationResult] = useState<any>(null);

  // Template preview and test email state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);

  // File upload state
  const [uploadedCredentials, setUploadedCredentials] = useState<File | null>(null);
  const [uploadedToken, setUploadedToken] = useState<File | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);

  const [formData, setFormData] = useState<EmailChannelData>({
    name: '',
    provider: 'imap_smtp',
    email_address: '',
    display_name: '',
    signature: '',
    provider_config: {},
    greeting_enabled: true,
    greeting_message: 'Hello! Thank you for contacting us via email. We will respond shortly.',
    auto_assignment_enabled: true,
    auto_reply_enabled: false,
    poll_interval: 300,

    // Template configuration defaults
    email_template_type: 'professional',
    response_tone: 'friendly',
    signature_position: 'bottom',
    include_branding: true,
    custom_template_html: '',
    custom_template_text: '',
    template_variables: {
      company_name: '',
      agent_name: '',
      support_email: '',
    },
  });

  useEffect(() => {
    loadProviders();
  }, []);

  useEffect(() => {
    if (formData.provider) {
      loadProviderConfig(formData.provider);
    }
  }, [formData.provider]);

  // Load existing channel data in edit mode
  useEffect(() => {
    if (isEditMode && channelId) {
      loadChannelData(channelId);
    }
  }, [channelId, isEditMode]);

  const loadProviders = async () => {
    try {
      const response = await emailChannelsAPI.getProviders();
      setProviders(response);
    } catch (error) {
      console.error('Failed to load Email providers:', error);
      setError('Failed to load providers');
    }
  };

  const loadProviderConfig = async (provider: string) => {
    try {
      const response = await emailChannelsAPI.getProviderConfig(provider);
      setConfigFields(response.config_fields);
    } catch (error) {
      console.error('Failed to load provider config:', error);
      setError('Failed to load provider configuration');
    }
  };

  const loadChannelData = async (id: number) => {
    setLoading(true);
    try {
      const channelData = await emailChannelsAPI.getChannel(id);

      // Populate form with existing data
      setFormData({
        name: channelData.name || '',
        provider: channelData.provider || 'imap_smtp',
        email_address: channelData.email_address || '',
        display_name: channelData.display_name || '',
        signature: channelData.signature || '',
        provider_config: channelData.provider_config || {},
        greeting_enabled: channelData.greeting_enabled ?? true,
        greeting_message: channelData.greeting_message || '',
        auto_assignment_enabled: channelData.auto_assignment_enabled ?? true,
        auto_reply_enabled: channelData.auto_reply_enabled ?? false,
        poll_interval: channelData.poll_interval || 300,

        // Template configuration
        email_template_type: channelData.email_template_type || 'professional',
        response_tone: channelData.response_tone || 'friendly',
        signature_position: channelData.signature_position || 'bottom',
        include_branding: channelData.include_branding ?? true,
        custom_template_html: channelData.custom_template_html || '',
        custom_template_text: channelData.custom_template_text || '',
        template_variables: channelData.template_variables || {
          company_name: '',
          agent_name: '',
          support_email: '',
        },
      });

      // Set created channel for verification step
      setCreatedChannel(channelData);

      // If already verified, show success
      if (channelData.is_verified) {
        setVerificationResult({ verified: true });
      }

    } catch (error: any) {
      console.error('Failed to load channel data:', error);
      setError(error.response?.data?.detail || 'Failed to load channel data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    if (field.startsWith('provider_config.')) {
      const configField = field.replace('provider_config.', '');
      setFormData(prev => ({
        ...prev,
        provider_config: {
          ...prev.provider_config,
          [configField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const togglePasswordVisibility = (field: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0: // Provider Selection
        return !!formData.provider;
      case 1: // Email Configuration
        if (!formData.email_address) return false;
        return Object.entries(configFields).every(([key, config]) => {
          if (!config.required) return true;
          const value = formData.provider_config[key];
          return value !== undefined && value !== null && value !== '';
        });
      case 2: // Template Configuration
        if (formData.email_template_type === 'custom') {
          return !!formData.custom_template_html && !!formData.custom_template_text;
        }
        return true;
      case 3: // Channel Settings
        return !!formData.name;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
      setError(null);
    } else {
      setError('Please fill in all required fields before proceeding.');
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) {
      setError('Please fill in all required fields.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let response;
      if (isEditMode && channelId) {
        // Update existing channel
        response = await emailChannelsAPI.updateChannel(channelId, formData);
        setSuccess('Email channel updated successfully!');
      } else {
        // Create new channel
        response = await emailChannelsAPI.createChannel(formData);
        setSuccess('Email channel created successfully!');
      }

      setCreatedChannel(response);
      setActiveStep(4);
    } catch (error: any) {
      setError(error.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'create'} Email channel`);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!createdChannel) return;

    setLoading(true);
    setError(null);

    try {
      const response = await emailChannelsAPI.verifyChannel(createdChannel.id);
      setVerificationResult(response);
      if (response.verified) {
        setSuccess('Email channel verified successfully!');
      } else {
        setError(response.error || 'Verification failed');
      }
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewTemplate = () => {
    setPreviewModalOpen(true);
  };

  const handleOpenTestEmailDialog = () => {
    setTestEmailDialogOpen(true);
  };

  const handleCredentialsUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadLoading(true);
    setError(null);
    try {
      const result = await emailChannelsAPI.parseCredentialsFile(file);
      if (result.valid && result.data) {
        // Auto-fill client_id and client_secret
        handleInputChange('provider_config.client_id', result.data.client_id);
        handleInputChange('provider_config.client_secret', result.data.client_secret);
        setUploadedCredentials(file);
        setSuccess(`Credentials file uploaded successfully! (${result.format})`);
      } else {
        setError(result.error || 'Invalid credentials file');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to upload credentials file');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleTokenUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadLoading(true);
    setError(null);
    try {
      const result = await emailChannelsAPI.parseTokenFile(file);
      if (result.valid && result.data) {
        // Auto-fill tokens and email
        if (result.data.oauth_token) {
          handleInputChange('provider_config.oauth_token', result.data.oauth_token);
        }
        if (result.data.oauth_refresh_token) {
          handleInputChange('provider_config.oauth_refresh_token', result.data.oauth_refresh_token);
        }
        if (result.data.email_address && !formData.email_address) {
          handleInputChange('email_address', result.data.email_address);
        }
        setUploadedToken(file);

        if (result.warning) {
          setSuccess(`Token file uploaded with warning: ${result.warning}`);
        } else {
          setSuccess('Token file uploaded successfully!');
        }
      } else {
        setError(result.error || 'Invalid token file');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to upload token file');
    } finally {
      setUploadLoading(false);
    }
  };

  const renderProviderSelection = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Email Provider
      </Typography>
      <Grid container spacing={2}>
        {Object.entries(providers).map(([key, provider]) => (
          <Grid item xs={12} md={6} key={key}>
            <Card 
              variant={formData.provider === key ? 'elevation' : 'outlined'}
              sx={{ 
                cursor: 'pointer',
                border: formData.provider === key ? '2px solid #1976d2' : undefined
              }}
              onClick={() => handleInputChange('provider', key)}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <Email sx={{ mr: 1 }} />
                  <Typography variant="h6">{provider.name}</Typography>
                </Box>
                <Typography color="text.secondary" paragraph>
                  {provider.description}
                </Typography>
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>Features:</Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {provider.features.map((feature, index) => (
                      <Chip key={index} label={feature} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Setup: {provider.setup_complexity}
                </Typography>
                {provider.use_cases && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {provider.use_cases}
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Launch />}
                  sx={{ mt: 1 }}
                  href={provider.documentation_url}
                  target="_blank"
                >
                  Documentation
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderConfigField = (key: string, config: ConfigField) => {
    const value = formData.provider_config[key] || config.default || '';
    
    if (config.type === 'select') {
      return (
        <FormControl fullWidth key={key}>
          <InputLabel>{config.label}</InputLabel>
          <Select
            value={value}
            label={config.label}
            onChange={(e) => handleInputChange(`provider_config.${key}`, e.target.value)}
            required={config.required}
          >
            {config.options?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (config.type === 'textarea') {
      return (
        <TextField
          key={key}
          label={config.label}
          value={value}
          onChange={(e) => handleInputChange(`provider_config.${key}`, e.target.value)}
          required={config.required}
          multiline
          rows={3}
          fullWidth
          helperText={config.description}
        />
      );
    }

    const inputType = config.type === 'password' ? (showPasswords[key] ? 'text' : 'password') : config.type;

    return (
      <TextField
        key={key}
        label={config.label}
        value={value}
        onChange={(e) => handleInputChange(`provider_config.${key}`, e.target.value)}
        required={config.required}
        fullWidth
        type={inputType}
        helperText={config.description}
        InputProps={config.type === 'password' ? {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => togglePasswordVisibility(key)} edge="end">
                {showPasswords[key] ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          )
        } : undefined}
      />
    );
  };

  const renderEmailConfiguration = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Configure {providers[formData.provider]?.name || 'Email'} Provider
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Email Address"
            value={formData.email_address}
            onChange={(e) => handleInputChange('email_address', e.target.value)}
            required
            fullWidth
            type="email"
            helperText="Your email address"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Display Name"
            value={formData.display_name || ''}
            onChange={(e) => handleInputChange('display_name', e.target.value)}
            fullWidth
            helperText="Name to display in outgoing emails (optional)"
          />
        </Grid>

        {/* Gmail OAuth File Upload Section */}
        {formData.provider === 'gmail' && (
          <>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 2 }}>
                <strong>Gmail OAuth Setup:</strong> Upload your credentials.json and token.json files OR enter OAuth details manually below.
                <br />
                <Typography variant="caption" component="span">
                  Need help? Check the{' '}
                  <a href="/GMAIL_OAUTH_SETUP_GUIDE.md" target="_blank" rel="noopener noreferrer">
                    Gmail OAuth Setup Guide
                  </a>
                </Typography>
              </Alert>
            </Grid>

            <Grid item xs={12} md={6}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={uploadedCredentials ? <CheckCircleOutline color="success" /> : <CloudUpload />}
                disabled={uploadLoading}
                color={uploadedCredentials ? 'success' : 'primary'}
              >
                {uploadedCredentials ? `✓ ${uploadedCredentials.name}` : 'Upload credentials.json'}
                <input
                  type="file"
                  hidden
                  accept=".json,application/json"
                  onChange={handleCredentialsUpload}
                />
              </Button>
              <Typography variant="caption" display="block" sx={{ mt: 0.5, ml: 1 }}>
                Contains Client ID & Client Secret
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={uploadedToken ? <CheckCircleOutline color="success" /> : <CloudUpload />}
                disabled={uploadLoading}
                color={uploadedToken ? 'success' : 'primary'}
              >
                {uploadedToken ? `✓ ${uploadedToken.name}` : 'Upload token.json'}
                <input
                  type="file"
                  hidden
                  accept=".json,application/json"
                  onChange={handleTokenUpload}
                />
              </Button>
              <Typography variant="caption" display="block" sx={{ mt: 0.5, ml: 1 }}>
                Contains Access Token & Refresh Token
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip label="OR ENTER MANUALLY" size="small" />
              </Divider>
            </Grid>
          </>
        )}

        {Object.entries(configFields).map(([key, config]) => (
          <Grid item xs={12} key={key}>
            {renderConfigField(key, config)}
          </Grid>
        ))}

        <Grid item xs={12}>
          <TextField
            label="Poll Interval (seconds)"
            value={formData.poll_interval}
            onChange={(e) => handleInputChange('poll_interval', parseInt(e.target.value) || 300)}
            type="number"
            fullWidth
            helperText="How often to check for new emails (300 = 5 minutes)"
          />
        </Grid>
      </Grid>

      {providers[formData.provider]?.documentation_url && (
        <Box mt={2}>
          <Button
            variant="outlined"
            startIcon={<Launch />}
            href={providers[formData.provider].documentation_url}
            target="_blank"
          >
            Setup Documentation
          </Button>
        </Box>
      )}
    </Box>
  );

  const renderTemplateConfiguration = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Email Template Configuration
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Template Type</InputLabel>
            <Select
              value={formData.email_template_type}
              label="Template Type"
              onChange={(e) => handleInputChange('email_template_type', e.target.value)}
            >
              <MenuItem value="professional">Professional</MenuItem>
              <MenuItem value="casual">Casual</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Response Tone</InputLabel>
            <Select
              value={formData.response_tone}
              label="Response Tone"
              onChange={(e) => handleInputChange('response_tone', e.target.value)}
            >
              <MenuItem value="friendly">Friendly</MenuItem>
              <MenuItem value="formal">Formal</MenuItem>
              <MenuItem value="neutral">Neutral</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Signature Position</InputLabel>
            <Select
              value={formData.signature_position}
              label="Signature Position"
              onChange={(e) => handleInputChange('signature_position', e.target.value)}
            >
              <MenuItem value="bottom">Bottom</MenuItem>
              <MenuItem value="top">Top</MenuItem>
              <MenuItem value="none">None</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.include_branding}
                onChange={(e) => handleInputChange('include_branding', e.target.checked)}
              />
            }
            label="Include Company Branding"
          />
        </Grid>

        {formData.email_template_type === 'custom' && (
          <>
            <Grid item xs={12}>
              <TextField
                label="Custom HTML Template"
                value={formData.custom_template_html}
                onChange={(e) => handleInputChange('custom_template_html', e.target.value)}
                multiline
                rows={8}
                fullWidth
                helperText="Use {{content}}, {{signature}}, {{company_name}} as placeholders"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Custom Plain Text Template"
                value={formData.custom_template_text}
                onChange={(e) => handleInputChange('custom_template_text', e.target.value)}
                multiline
                rows={6}
                fullWidth
                helperText="Plain text version of your email template"
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Template Variables
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Company Name"
                value={formData.template_variables.company_name || ''}
                onChange={(e) => handleInputChange('template_variables', {
                  ...formData.template_variables,
                  company_name: e.target.value
                })}
                fullWidth
                helperText="Your company or brand name"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Agent Name"
                value={formData.template_variables.agent_name || ''}
                onChange={(e) => handleInputChange('template_variables', {
                  ...formData.template_variables,
                  agent_name: e.target.value
                })}
                fullWidth
                helperText="Default agent name for responses"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Support Email"
                value={formData.template_variables.support_email || ''}
                onChange={(e) => handleInputChange('template_variables', {
                  ...formData.template_variables,
                  support_email: e.target.value
                })}
                fullWidth
                type="email"
                helperText="Your support email address"
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<Visibility />}
            onClick={handlePreviewTemplate}
          >
            Preview Template
          </Button>
        </Grid>

        <Grid item xs={12} md={6}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<Send />}
            onClick={handleOpenTestEmailDialog}
            disabled={!createdChannel}
          >
            Send Test Email
          </Button>
        </Grid>
      </Grid>
    </Box>
  );

  const renderChannelSettings = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Email Channel Settings
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Channel Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            required
            fullWidth
            helperText="A descriptive name for your Email channel"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.auto_reply_enabled}
                onChange={(e) => handleInputChange('auto_reply_enabled', e.target.checked)}
              />
            }
            label="Enable Auto-Reply"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.greeting_enabled}
                onChange={(e) => handleInputChange('greeting_enabled', e.target.checked)}
              />
            }
            label="Enable Greeting Message"
          />
        </Grid>

        {formData.greeting_enabled && (
          <Grid item xs={12}>
            <TextField
              label="Auto-Reply Message"
              value={formData.greeting_message}
              onChange={(e) => handleInputChange('greeting_message', e.target.value)}
              required
              multiline
              rows={3}
              fullWidth
              helperText="Automatic response message for incoming emails"
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.auto_assignment_enabled}
                onChange={(e) => handleInputChange('auto_assignment_enabled', e.target.checked)}
              />
            }
            label="Auto-assign Email Conversations"
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderVerification = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Email Channel Created
      </Typography>
      
      {createdChannel && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {createdChannel.name}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Provider:</Typography>
                <Typography>{createdChannel.provider}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Email Address:</Typography>
                <Typography>{createdChannel.email_address}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Status:</Typography>
                <Box display="flex" alignItems="center">
                  {createdChannel.is_verified ? (
                    <CheckCircle color="success" sx={{ mr: 1 }} />
                  ) : (
                    <Error color="warning" sx={{ mr: 1 }} />
                  )}
                  <Typography>
                    {createdChannel.is_verified ? 'Verified' : 'Not Verified'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Poll Interval:</Typography>
                <Typography>{createdChannel.poll_interval} seconds</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Button
        variant="contained"
        onClick={handleVerify}
        disabled={loading || !createdChannel}
        startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
        fullWidth
      >
        {loading ? 'Testing Connection...' : 'Test Email Connection'}
      </Button>

      {verificationResult && (
        <>
          <Alert
            severity={verificationResult.verified ? 'success' : 'error'}
            sx={{ mt: 2 }}
          >
            {verificationResult.verified ? (
              'Email channel verified successfully! Connection to email server established.'
            ) : (
              `Verification failed: ${verificationResult.error}`
            )}
          </Alert>

          {verificationResult.verified && onClose && (
            <Button
              variant="contained"
              color="primary"
              onClick={onClose}
              fullWidth
              sx={{ mt: 2 }}
            >
              Done
            </Button>
          )}
        </>
      )}
    </Box>
  );

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <Email sx={{ mr: 2 }} />
          <Typography variant="h5">
            {isEditMode ? 'Edit Email Channel' : 'Setup Email Channel'}
          </Typography>
        </Box>
        {onClose && (
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
            <StepContent>
              {index === 0 && renderProviderSelection()}
              {index === 1 && renderEmailConfiguration()}
              {index === 2 && renderTemplateConfiguration()}
              {index === 3 && renderChannelSettings()}
              {index === 4 && renderVerification()}

              <Box sx={{ mb: 2, mt: 2 }}>
                <div>
                  {index < 4 && (
                    <Button
                      variant="contained"
                      onClick={index === 3 ? handleSubmit : handleNext}
                      disabled={loading || !validateStep(index)}
                      sx={{ mt: 1, mr: 1 }}
                      startIcon={loading ? <CircularProgress size={20} /> : undefined}
                    >
                      {loading ? 'Processing...' : (index === 3 ? (isEditMode ? 'Update Channel' : 'Create Channel') : 'Continue')}
                    </Button>
                  )}
                  {index > 0 && index < 4 && (
                    <Button
                      disabled={loading}
                      onClick={handleBack}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Back
                    </Button>
                  )}
                </div>
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>

      {/* Template Preview Modal */}
      <TemplatePreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        channelId={createdChannel?.id}
        previewData={{
          template_type: formData.email_template_type,
          custom_template_html: formData.custom_template_html,
          custom_template_text: formData.custom_template_text,
          variables: formData.template_variables,
          response_tone: formData.response_tone,
          signature_position: formData.signature_position,
          include_branding: formData.include_branding,
        }}
      />

      {/* Test Email Dialog */}
      <TestEmailDialog
        open={testEmailDialogOpen}
        onClose={() => setTestEmailDialogOpen(false)}
        channelId={createdChannel?.id}
      />
    </Paper>
  );
};

export default EmailChannelConfig;